/**
 * AppSidebar Component (Main Sidebar Overlay)
 * Phase 68 M4: Full-viewport overlay sidebar per locked design 001-global-shell.
 *
 * Per L3: Always overlay, no pinning. Triggered by hamburger.
 * Per L4/L14: NO user section here — user stuff in Header popup.
 * Per L12: Full viewport height.
 *
 * Design Pattern:
 * - Always dark background (#111111) regardless of theme
 * - Close button on LEFT (40x40px), aligns with hamburger
 * - Top bar 52px (matches header height)
 * - App items with flyout submenus on hover
 */

import React, { useState, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Popper,
  Grow,
} from '@mui/material'
import { useTheme, styled } from '@mui/material/styles'
import Chat from '@mui/icons-material/Chat'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Close from '@mui/icons-material/Close'
import LibraryBooks from '@mui/icons-material/LibraryBooks'
import Settings from '@mui/icons-material/Settings'
import Work from '@mui/icons-material/Work'

import { useAuthContext } from '@contexts/AuthContext'
import { useAppAccess } from '@/hooks/useAppAccess'
import { APP_KEYS, type AppKey } from '@/config/appKeys'
import logoImage from '@/assets/transparent-logo.png'
import { ADMIN_SIDEBAR, CANTERA_SIDEBAR } from '@/config/sidebarConfigs'

// ============================================================================
// Types
// ============================================================================

interface NavSubItem {
  text: string
  path: string
  icon?: React.ReactNode
}

interface NavItem {
  id: string
  text: string
  path?: string // Optional for items with subitems
  defaultPath?: string // For flyout items: where to navigate on click (defaults to first subitem)
  icon: React.ReactNode
  subitems?: NavSubItem[]
}

interface NavSection {
  id: string
  title?: string // Optional section title (for visual grouping)
  items: NavItem[]
  separator?: 'before' | 'after' | 'both'
}

// ============================================================================
// Navigation Configuration
// ============================================================================

/**
 * Navigation sections for main sidebar per locked design prototype lines 1331-1368
 * Only three apps: Compás, Cantera, Administración
 * Per L4/L14: NO profile/user items here — user stuff in Header popup
 *
 * Phase 76: Sections and subitems filtered by app access instead of is_admin/isSuperAdmin.
 * Phase 70: Administración subitems derived from ADMIN_SIDEBAR config (single source of truth)
 */
function getNavSections(
  hasAccess: (appKey: AppKey) => boolean,
  hasAnyAdminAccess: boolean,
): NavSection[] {
  // Phase 76: Derive admin subitems from ADMIN_SIDEBAR config, filtered by requiredApp
  const adminSubitems: NavSubItem[] = ADMIN_SIDEBAR.items
    .filter((item) => {
      if (item.requiredApp && !hasAccess(item.requiredApp)) return false
      return true
    })
    .map((item) => ({
      text: item.label,
      path: item.path,
    }))

  const items: NavItem[] = []

  // Compás — visible if user has compas access; Knowledge subitem if has knowledge access
  if (hasAccess(APP_KEYS.COMPAS)) {
    const compasSubitems: NavSubItem[] = [{ text: 'Chat', path: '/chat' }]
    if (hasAccess(APP_KEYS.KNOWLEDGE)) {
      compasSubitems.push({ text: 'Gestión de Documentos', path: '/knowledge' })
    }
    items.push(
      compasSubitems.length > 1
        ? {
            id: 'compas',
            text: 'Compás',
            icon: <Chat fontSize="small" />,
            defaultPath: '/chat',
            subitems: compasSubitems,
          }
        : {
            id: 'compas',
            text: 'Compás',
            icon: <Chat fontSize="small" />,
            path: '/chat',
          },
    )
  }

  // Cantera — visible if user has cantera access
  if (hasAccess(APP_KEYS.CANTERA)) {
    const canteraSubitems: NavSubItem[] = CANTERA_SIDEBAR.items.map((item) => ({
      text: item.label,
      path: item.path,
    }))

    items.push({
      id: 'cantera',
      text: 'Cantera',
      icon: <Work fontSize="small" />,
      defaultPath: canteraSubitems[0]?.path || '/talent-pipeline',
      subitems: canteraSubitems,
    })
  }

  // Administración — visible if user has any admin app access
  if (hasAnyAdminAccess && adminSubitems.length > 0) {
    items.push({
      id: 'admin-section',
      text: 'Administración',
      icon: <Settings fontSize="small" />,
      defaultPath: adminSubitems[0]?.path || '/admin/users',
      subitems: adminSubitems,
    })
  }

  return [{ id: 'apps', items }]
}

// ============================================================================
// Styled Components — Always dark per prototype (#111111)
// ============================================================================

const SidebarRoot = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%', // Full height within Drawer
  width: '100%',
  backgroundColor: '#111111', // Always dark per prototype
  color: '#e0e0e0',
  overflow: 'visible', // Allow flyouts to overflow
}))

/**
 * App item button — per prototype lines 553-608
 * Always dark styling: #111111 background, #ddd text, colored icon badges
 */
const AppItemButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'appColor',
})<{ appColor?: string }>(({ appColor }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 14, // 14px per prototype
  padding: '14px 20px', // per prototype
  cursor: 'pointer',
  transition: 'background 0.15s ease',
  position: 'relative',

  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.06)',
  },

  // Icon badge
  '& .app-icon': {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: appColor ? `${appColor}26` : 'rgba(155,157,159,0.15)', // 15% opacity
    color: appColor || '#9B9D9F',
    '& svg': {
      width: 20,
      height: 20,
    },
  },

  // Label
  '& .app-label': {
    fontSize: 15,
    fontWeight: 500,
    color: '#ddd',
  },

  // Arrow
  '& .app-arrow': {
    marginLeft: 'auto',
    color: '#555',
    display: 'flex',
    alignItems: 'center',
    transition: 'color 0.15s ease',
    '& svg': {
      width: 16,
      height: 16,
    },
  },

  '&:hover .app-arrow': {
    color: '#999',
  },
}))

/**
 * Flyout menu — per prototype lines 609-658
 * Dark styling: #1a1a1a background
 */
const FlyoutMenuContent = styled(Paper)(() => ({
  minWidth: 250, // 250px per prototype
  backgroundColor: '#1a1a1a',
  borderRadius: 8,
  boxShadow: '4px 0 24px rgba(0,0,0,0.3)',
  borderLeft: '1px solid rgba(255,255,255,0.06)',
  overflow: 'hidden',
}))

const FlyoutHeader = styled(Box)(() => ({
  padding: '16px 20px 12px',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: '#666',
  fontWeight: 600,
  borderBottom: '1px solid rgba(255,255,255,0.04)',
}))

const FlyoutItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 20px',
  fontSize: 13,
  color: isActive ? '#fff' : '#b0b0b0',
  cursor: 'pointer',
  transition: 'all 0.12s ease',
  backgroundColor: isActive ? 'rgba(255,255,255,0.06)' : 'transparent',

  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.06)',
    color: '#fff',
  },
}))

// ============================================================================
// App Colors — per prototype icon badges
// ============================================================================

const APP_COLORS = {
  compas: '#4F9FCF',
  cantera: '#D4565F',
  admin: '#9B9D9F',
}

// ============================================================================
// Component
// ============================================================================

interface AppSidebarProps {
  onClose?: () => void
  onNavigate?: (path: string) => void  // BUG-027: Deferred navigation via parent
}

const AppSidebar: React.FC<AppSidebarProps> = ({ onClose, onNavigate }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const { user } = useAuthContext()
  const { hasAccess, hasAnyAdminAccess } = useAppAccess()

  const [activeHoverId, setActiveHoverId] = useState<string | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Phase 76: Navigation sections filtered by app access (replaces isAdmin/isSuperAdmin)
  const navSections = useMemo(
    () => getNavSections(hasAccess, hasAnyAdminAccess),
    [hasAccess, hasAnyAdminAccess],
  )

  // Check if a path is active
  const isPathActive = useCallback(
    (path: string) => {
      if (path === '/') return location.pathname === '/'
      return location.pathname === path || location.pathname.startsWith(`${path}/`)
    },
    [location.pathname]
  )

  // Check if any subitem is active
  const hasActiveSubitem = useCallback(
    (subitems?: NavSubItem[]) => subitems?.some((sub) => isPathActive(sub.path)),
    [isPathActive]
  )

  // Handle navigation
  // BUG-027 fix: Use onNavigate (deferred) if provided, otherwise direct navigate
  const handleNavigate = useCallback(
    (path: string) => {
      setActiveHoverId(null)
      if (onNavigate) {
        // Deferred: parent handles close animation then navigates
        onNavigate(path)
      } else {
        // Fallback: direct navigation (legacy behavior)
        navigate(path)
        onClose?.()
      }
    },
    [navigate, onClose, onNavigate]
  )

  // Handle hover enter on nav item (sets anchor)
  const handleHoverEnter = useCallback((id: string, element: HTMLElement) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setActiveHoverId(id)
    setAnchorEl(element)
  }, [])

  // Handle hover enter on flyout (keeps menu open without changing anchor)
  const handleFlyoutEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }, [])

  // Handle hover leave with delay
  const handleHoverLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setActiveHoverId(null)
      setAnchorEl(null)
    }, 150)
  }, [])

  // Render flyout menu content (used by Popper) — per prototype lines 609-658
  const renderFlyoutContent = (item: NavItem) => (
    <FlyoutMenuContent
      elevation={0}
      onMouseEnter={handleFlyoutEnter}
      onMouseLeave={handleHoverLeave}
    >
      <FlyoutHeader>{item.text}</FlyoutHeader>
      <Box sx={{ py: 1 }}>
        {item.subitems!.map((subitem) => (
          <FlyoutItem
            key={subitem.path}
            isActive={isPathActive(subitem.path)}
            onClick={() => handleNavigate(subitem.path)}
          >
            {subitem.text}
          </FlyoutItem>
        ))}
      </Box>
    </FlyoutMenuContent>
  )

  // Get app color for icon badge
  const getAppColor = (id: string): string => {
    if (id === 'compas') return APP_COLORS.compas
    if (id === 'cantera') return APP_COLORS.cantera
    if (id.includes('admin')) return APP_COLORS.admin
    return APP_COLORS.admin
  }

  // Render navigation item — per prototype lines 553-608
  const renderNavItem = (item: NavItem) => {
    const hasSubmenu = item.subitems && item.subitems.length > 0
    const isHovered = activeHoverId === item.id
    const appColor = getAppColor(item.id)

    // Items without submenu - simple nav, direct click
    if (!hasSubmenu) {
      return (
        <AppItemButton
          key={item.id}
          appColor={appColor}
          onClick={() => {
            if (item.path) handleNavigate(item.path)
          }}
        >
          <span className="app-icon">{item.icon}</span>
          <span className="app-label">{item.text}</span>
        </AppItemButton>
      )
    }

    // Items WITH submenu - use Popper for flyout
    return (
      <React.Fragment key={item.id}>
        <AppItemButton
          appColor={appColor}
          onClick={() => {
            const targetPath = item.defaultPath || item.subitems?.[0]?.path
            if (targetPath) handleNavigate(targetPath)
          }}
          onMouseEnter={(e) => handleHoverEnter(item.id, e.currentTarget as HTMLElement)}
          onMouseLeave={handleHoverLeave}
        >
          <span className="app-icon">{item.icon}</span>
          <span className="app-label">{item.text}</span>
          <span className="app-arrow">
            <ChevronRight />
          </span>
        </AppItemButton>

        {/* Flyout via Popper - renders to document.body, escapes overflow clipping */}
        <Popper
          open={isHovered}
          anchorEl={anchorEl}
          placement="right-start"
          transition
          sx={{ zIndex: theme.zIndex.tooltip + 100 }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} timeout={200}>
              <Box>
                {renderFlyoutContent(item)}
              </Box>
            </Grow>
          )}
        </Popper>
      </React.Fragment>
    )
  }

  return (
    <SidebarRoot component="aside">
      {/* Top Bar — 52px, close button LEFT + brand per prototype lines 506-528 */}
      <Box
        sx={{
          height: 52,
          minHeight: 52,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5, // 4px
          px: 1, // 8px left (matches header hamburger position)
          pr: 1.5, // 12px right
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {/* Close button — 40x40px, LEFT side per prototype */}
        <IconButton
          onClick={onClose}
          aria-label="cerrar menu"
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1, // 8px
            color: '#999',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.08)',
              color: '#fff',
            },
          }}
        >
          <Close sx={{ fontSize: 20 }} />
        </IconButton>

        {/* Brand — logo + NEXO text */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            component="img"
            src={logoImage}
            alt="NEXO Logo"
            sx={{ width: 28, height: 28, objectFit: 'contain' }}
          />
          <Typography
            sx={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 22, // 22px per prototype
              letterSpacing: '0.04em',
              color: '#f0f0f0',
              lineHeight: 1,
              pt: 0.25, // 2px vertical alignment
            }}
          >
            NEXO
          </Typography>
        </Box>
      </Box>

      {/* Navigation Section — per prototype lines 549-608 */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 2 }}>
        {/* Phase 76: Items pre-filtered by app access in getNavSections */}
        {navSections.map((section) => {
          if (section.items.length === 0) return null

          return (
            <React.Fragment key={section.id}>
              {section.items.map(renderNavItem)}
            </React.Fragment>
          )
        })}
      </Box>

      {/* Per L4/L14: NO user section here — user stuff in Header popup */}
    </SidebarRoot>
  )
}

export default AppSidebar
